<template>
  <v-container fluid v-if="events.length > 0" class="calendar-container">
    <v-row>
      <v-toolbar flat color="white">
        <v-toolbar-title v-if="$refs.calendar">
          {{ $refs.calendar.title }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="setToday">
          Today
        </v-btn>
      </v-toolbar>
    </v-row>
    <v-row justify="center" class="calendar-row">
      <v-col cols="12">
        <v-sheet :height="$vuetify.breakpoint.xsOnly ? '' : '100%'">
          <v-calendar
            ref="calendar"
            :type="type"
            color="secondary"
            :events="events"
            v-model="focus"
            :event-margin-bottom="3"
            :now="today"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @change="updateRange"
          />

          <v-menu
            v-model="selectedOpen"
            :close-on-content-click="false"
            :activator="selectedElement"
            offset-x
          >
            <v-card color="grey lighten-4" min-width="350px" flat>
              <v-toolbar color="primary" dark>
                <v-toolbar-title v-html="selectedEvent.name"/>
                <v-spacer/>
                <v-btn icon v-if="selectedEvent.attributes && selectedEvent.attributes.url">
                  <v-icon>fa-external-link</v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-subtitle>
                <span v-if="selectedEvent.startDate">{{ selectedEvent.startDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) }}</span>
                <span> - </span>
                <span v-if="selectedEvent.endDate">{{ selectedEvent.endDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) }}</span>
              </v-card-subtitle>

              <v-card-text v-if="selectedEvent.description">{{ selectedEvent.description }}</v-card-text>
              <v-card-text>
                <div v-if="selectedEvent.location">Location: {{ selectedEvent.location }}</div>
                <div v-if="selectedEvent.organization">Organization: {{ selectedEvent.organization }}</div>
              </v-card-text>

              <v-card-text v-if="selectedEvent.attributes && (
                selectedEvent.attributes.foodProvided ||
                selectedEvent.attributes.alcoholProvided ||
                selectedEvent.attributes.ticketRequired ||
                selectedEvent.attributes.invitationOnly ||
                selectedEvent.attributes.openToPublic ||
                selectedEvent.attributes.openToAlumni
              )">
                <div v-if="selectedEvent.attributes.foodProvided">Food Provided</div>
                <div v-if="selectedEvent.attributes.alcoholProvided">Alcohol Provided</div>

                <div v-if="selectedEvent.attributes.ticketRequired">Ticket Required</div>
                <div v-if="selectedEvent.attributes.invitationOnly">Invitation Only</div>
                <div v-if="selectedEvent.attributes.studentsOnly">UST Students Only</div>
                <div v-if="selectedEvent.attributes.openToPublic">Open to the Public</div>
                <div v-if="selectedEvent.attributes.openToAlumni">Open to Alumni</div>
              </v-card-text>

              <v-card-actions>
                <v-btn text color="secondary" @click="selectedOpen = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row justify="center">
      <!-- Previous Arrow -->
      <v-col v-if="!$vuetify.breakpoint.xsOnly" cols="3" sm="3">
        <v-btn @click="$refs.calendar.prev()" block color="primary">
          <v-icon dark left>fa-arrow-left</v-icon>
          Prev
        </v-btn>
      </v-col>

      <!-- Calendar Type Select -->
      <v-col cols="12" sm="4">
        <v-select v-model="type" :items="typeOptions" label="Type"/>
      </v-col>

      <!-- Previous Arrow (Mobile) -->
      <v-col v-if="$vuetify.breakpoint.xsOnly" cols="6" class="text-sm-left text-xs-center">
        <v-btn @click="$refs.calendar.prev()" block color="primary">
          <v-icon dark left>fa-arrow-left</v-icon>
          Prev
        </v-btn>
      </v-col>

      <!-- Next Arrow -->
      <v-col cols="6" sm="3" class="text-sm-right text-xs-center">
        <v-btn @click="$refs.calendar.next()" block color="primary">
          Next
          <v-icon right dark>fa-arrow-right</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </v-container>

  <v-container fluid v-else-if="loadingReservations" class="loader-container">
    <v-row align="center" justify="center">
      <v-col cols="4">
        <v-card color="primary" dark>
          <v-card-text>
            Loading...
            <v-progress-linear indeterminate color="white" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <v-container fluid v-else class="loader-container">
    <v-row align="center" justify="center">
      <v-col cols="4">
        <v-card color="error" dark>
          <v-card-text>
            No events found
            <v-progress-linear color="white" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'Events',
  data () {
    return {
      typeOptions: [
        { text: 'Day', value: 'day' },
        { text: 'Week', value: 'week' },
        { text: 'Month', value: 'month' }
      ],
      type: 'week',
      start: '',
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      today: '',
      focus: ''
    }
  },
  computed: {
    ...mapGetters([
      'selectedLocation',
      'reservations',
      'loadingReservations'
    ]),
    events () {
      if (!this.reservations) return []

      return this.reservations
        .filter(reservation => {
          let include = true
          const event = reservation['r25:event']

          if (event['r25:custom_attribute']) {
            let attributes = []

            if (!event['r25:custom_attribute'].length) {
              attributes = [event['r25:custom_attribute']]
            } else {
              attributes = event['r25:custom_attribute']
            }

            attributes.forEach(attribute => {
              const id = attribute['r25:attribute_id']
              const value = attribute['r25:attribute_value']

              switch (id) {
                case 20: { // "Hide event on web calendars"
                  include = !(value === 'T')
                  break
                }
              }
            })

            return include
          }
        })
        .map(reservation => {
          const event = reservation['r25:event']

          const start = new Date(reservation['r25:event_start_dt'])
          const end = new Date(reservation['r25:event_end_dt'])

          const startFormatted = `${reservation['r25:event_start_dt'].substring(0, 10)} ${reservation['r25:event_start_dt'].substring(11, 16)}`
          const endFormatted = `${reservation['r25:event_end_dt'].substring(0, 10)} ${reservation['r25:event_end_dt'].substring(11, 16)}`

          const name = event['r25:event_name']

          const attributesOut = {}

          if (event['r25:custom_attribute']) {
            let attributes = []

            if (!event['r25:custom_attribute'].length) {
              attributes = [event['r25:custom_attribute']]
            } else {
              attributes = event['r25:custom_attribute']
            }

            attributes.forEach(attribute => {
              const id = attribute['r25:attribute_id']
              const value = attribute['r25:attribute_value']

              switch (id) {
                case -1: { // Website
                  attributesOut.url = value
                  break
                }

                case 11: { // "Tickets - required"
                  attributesOut.ticketRequired = (value === 'T')
                  break
                }

                case 16: { // "Contact  AT event if not requestor"
                  attributesOut.contact = value
                  break
                }

                case 21: { // "Event is open to the public"
                  attributesOut.openToPublic = (value === 'T')
                  break
                }

                case 22: { // "Event is Invitation only"
                  attributesOut.inviteOnly = (value === 'T')
                  break
                }

                case 24: { // "UST Catering will provide food"
                  attributesOut.foodProvided = (value === 'T')
                  break
                }

                case 25: { // "UST Catering will provide alcohol"
                  attributesOut.alcoholProvided = (value === 'T')
                  break
                }

                case 49: { // "Event is for UST students only"
                  attributesOut.studentsOnly = (value === 'T')
                  break
                }

                case 85: { // "Event is open to Alumni"
                  attributesOut.openToAlumni = (value === 'T')
                  break
                }
              }
            })
          }

          let description = null
          if (event['r25:event_text'] && event['r25:event_text'][0] && event['r25:event_text'][0]['r25:text']) {
            description = event['r25:event_text'][0]['r25:text'].replace(/<[^>]*>?/gm, '')
          }

          let location = null
          if (reservation['r25:space_reservation'] && reservation['r25:space_reservation'][0] && reservation['r25:space_reservation'][0]['r25:space'] && reservation['r25:space_reservation'][0]['r25:space']['r25:formal_name']) {
            location = reservation['r25:space_reservation'][0]['r25:space']['r25:formal_name']
          }

          let organization = null
          if (event['r25:organization'] && event['r25:organization']['r25:organization_name']) {
            organization = event['r25:organization']['r25:organization_name']
          }

          return {
            name,
            start: startFormatted,
            end: endFormatted,
            startDate: start,
            endDate: end,
            description,
            location,
            organization,
            attributes: attributesOut
          }
        })
    }
  },
  watch: {
    selectedLocation () {
      this.onSelectedLocationChange()
    }
  },
  created () {
    const today = new Date()
    const todayFormatted = `${today.toISOString().substring(0, 10)} ${today.toISOString().substring(11, 16)}`
    this.today = todayFormatted
    this.setToday()

    if (this.$vuetify.breakpoint.smAndDown) {
      this.type = 'day'
    }

    this.onSelectedLocationChange()
  },
  methods: {
    ...mapActions([
      'loadReservations'
    ]),
    ...mapMutations([
      'setReservations'
    ]),
    viewDay ({ date }) {
      this.focus = date
      this.type = 'day'
    },
    setToday () {
      this.focus = this.today
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () {
      this.$refs.calendar.next()
    },
    showEvent ({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        setTimeout(() => { this.selectedOpen = true }, 10)
      }

      if (this.selectedOpen) {
        this.selectedOpen = false
        setTimeout(open, 10)
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    },
    updateRange ({ start, end }) {
      this.start = start
      this.end = end
    },
    nth (d) {
      return d > 3 && d < 21
        ? 'th'
        : ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'][d % 10]
    },
    onSelectedLocationChange () {
      if (this.reservations) {
        this.setReservations([])
      }

      if (this.selectedLocation) {
        this.loadReservations(this.selectedLocation.spaces)
      }
    }
  }
}
</script>

<style lang="scss">
.calendar-container {
  &.container {
    height: 100%;
  }

  .calendar-row {
    .col {
      height: 100%;
    }
  }
}

.loader-container {
  &.container, .row {
    height: 100%;
  }
}

@media only screen and (min-width: 960px) {
  .calendar-container .calendar-row {
    height: calc(100% - 100px);
  }
}
</style>
